import { HOST_VIDEO_API } from 'config';
import { rtkApi } from 'redux/rtkApi';
import { ListParams } from 'types/common';
import {
  IMarathonComment,
  IMarathonCommentComplaint,
  IMarathonCommentComplaintListResponse,
  IMarathonCommentListResponse,
  IMarathonCommentUpdateRequest,
} from 'types/marathonComment';
import { MarathonReviewStatus } from 'types/marathonReview';

export type MarathonCommentsListParams = ListParams & {
  scan?: string;
  status?: MarathonReviewStatus[];
  group_id?: number;
};

export type MarathonCommentComplaintsListParams = {
  user_id?: 'y' | 'n';
  page?: number;
  per_page?: number;
};

const marathonComments = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getMarathonCommentsList: build.query<IMarathonCommentListResponse, MarathonCommentsListParams>({
      query: (params) => ({
        url: `${HOST_VIDEO_API}admin/comments`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: IMarathonCommentListResponse) => response,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map((c) => ({ type: 'MarathonComments' as const, id: c.id })),
              { type: 'MarathonComments', id: 'LIST' },
            ]
          : [{ type: 'MarathonComments', id: 'LIST' }],
    }),

    updateMarathonComment: build.mutation<
      IMarathonComment,
      { id: number; data: IMarathonCommentUpdateRequest }
    >({
      query: ({ id, data }) => ({
        url: `${HOST_VIDEO_API}admin/comments/${id}`,
        method: 'PUT',
        data,
      }),
      transformResponse: (response: { data: IMarathonComment }) => response.data,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'MarathonComments', id },
        { type: 'MarathonComments', id: 'LIST' },
      ],
    }),

    deleteMarathonComment: build.mutation<void, number>({
      query: (id) => ({
        url: `${HOST_VIDEO_API}admin/comments/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'MarathonComments', id }],
    }),

    banMarathonComment: build.mutation<IMarathonComment, { id: number; ban_text?: boolean }>({
      query: ({ id, ...data }) => ({
        url: `${HOST_VIDEO_API}admin/comments/${id}/ban`,
        method: 'POST',
        data,
      }),
      transformResponse: (response: { data: IMarathonComment }) => response.data,
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'MarathonComments', id },
        { type: 'MarathonComments', id: 'LIST' },
      ],
    }),

    getMarathonCommentComplaints: build.query<
      IMarathonCommentComplaintListResponse,
      MarathonCommentComplaintsListParams
    >({
      query: (params) => ({
        url: `${HOST_VIDEO_API}admin/comments-complaints`,
        method: 'GET',
        params,
      }),
      transformResponse: (response: IMarathonCommentComplaintListResponse) => response,
      providesTags: [{ type: 'MarathonCommentComplaints', id: 'LIST' }],
    }),

    processMarathonCommentComplaint: build.mutation<IMarathonCommentComplaint, number>({
      query: (id) => ({
        url: `${HOST_VIDEO_API}admin/comments-complaints/${id}`,
        method: 'PUT',
      }),
      transformResponse: (response: { data: IMarathonCommentComplaint }) => response.data,
      invalidatesTags: [{ type: 'MarathonCommentComplaints', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetMarathonCommentsListQuery,
  useUpdateMarathonCommentMutation,
  useDeleteMarathonCommentMutation,
  useBanMarathonCommentMutation,
  useGetMarathonCommentComplaintsQuery,
  useProcessMarathonCommentComplaintMutation,
} = marathonComments;
